import {OverlayContainer} from '@angular/cdk/overlay';
import {AsyncPipe, NgClass} from '@angular/common';
import {Component, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, NavigationStart, Router, RouterModule} from '@angular/router';
import {IJwtUser} from '@evencel/models';
import {Store} from '@ngxs/store';
import {delay, fromEvent, tap} from 'rxjs';
import {AppState, ShowLoading} from './states';

interface IStorageData {
    user: IJwtUser;
}

@Component({
    standalone: true,
    imports: [RouterModule, NgClass, AsyncPipe],
    selector: 'evencel-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent {
    store = inject(Store);
    router = inject(Router);

    overlayContainer = inject(OverlayContainer);

    protected loading$ = this.store.select(AppState.loading);
    protected darkMode$ = this.store.select(AppState.darkMode);
    private darkTheme = 'dark-theme';

    constructor() {
        this.store.dispatch(new ShowLoading(false));
        const classList = this.overlayContainer.getContainerElement().classList;
        this.darkMode$
            .pipe(
                takeUntilDestroyed(),
                tap(dark => {
                    dark ? classList.add(this.darkTheme) : classList.remove(this.darkTheme);
                })
            )
            .subscribe();

        // Check for user changes in local storage for the user to reload the page
        fromEvent(window, 'storage')
            .pipe(
                takeUntilDestroyed(),
                tap(async (event: Event) => {
                    const se = event as StorageEvent;
                    if (se.newValue && se.oldValue) {
                        const newValue: IStorageData = JSON.parse(se.newValue);
                        const oldValue: IStorageData = JSON.parse(se.oldValue);
                        if (newValue.user._id !== oldValue.user._id) {
                            await this.router.navigate(['/admin/dashboard']);
                            window.location.reload();
                        }
                    }
                })
            )
            .subscribe();

        // Show loading spinner on navigation start and hide on navigation end
        this.router.events
            .pipe(
                delay(0),
                tap(event => {
                    const durtyFormIndex = this.store.selectSnapshot(AppState.durtyFormIndex);
                    const formValid = durtyFormIndex < 0;

                    const start = event instanceof NavigationStart;

                    if (event instanceof NavigationEnd) {
                        this.store.dispatch(new ShowLoading(false));
                    } else if (start && formValid) {
                        this.store.dispatch(new ShowLoading(true));
                    }
                }),
                takeUntilDestroyed()
            )
            .subscribe();
    }
}
